import {
  DataType,
  EditableRefKind,
  FieldLevel,
  FieldSection,
  ListItemType,
  type AdminField,
  type AdminMeta,
  type FieldCreateInput,
  type LocalizedName,
} from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { useMemo, useState } from 'react'

import {
  ActiveToggle,
  AdminError,
  NameInputs,
  OriginBadge,
  RemoveButton,
  useAdminAction,
} from './common.js'

type Ui = FieldCreateInput['ui']

export function FieldsTab({ data }: { data: AdminMeta }) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<AdminField | 'new' | null>(null)
  const rows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('ru')
    return q
      ? data.fields.filter((f) => `${f.name.ru} ${f.code}`.toLocaleLowerCase('ru').includes(q))
      : data.fields
  }, [data.fields, query])

  return (
    <div className="grid gap-6">
      {editing === 'new' ? (
        <FieldForm data={data} onDone={() => setEditing(null)} />
      ) : editing ? (
        <FieldForm key={editing.code} data={data} field={editing} onDone={() => setEditing(null)} />
      ) : null}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center gap-3">
          <CardTitle className="mr-auto">{t('adminMeta.tabs.fields')}</CardTitle>
          <Input
            className="w-56"
            type="search"
            aria-label={t('common.search')}
            placeholder={t('common.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button size="sm" onClick={() => setEditing('new')}>
            {t('adminMeta.fields.new')}
          </Button>
        </CardHeader>
        <CardContent className="grid gap-3">
          <AdminError error={action.error} />
          <Table>
            <THead>
              <Tr>
                <Th>{t('admin.refFields.name')}</Th>
                <Th>{t('admin.dataType')}</Th>
                <Th>{t('admin.level')}</Th>
                <Th>{t('adminMeta.usageColumn')}</Th>
                <Th />
              </Tr>
            </THead>
            <TBody>
              {rows.map((f) => (
                <Tr key={f.code}>
                  <Td>
                    <button
                      type="button"
                      className={`text-left hover:text-primary ${f.active ? '' : 'text-muted-foreground line-through'}`}
                      onClick={() => setEditing(f)}
                    >
                      {f.name.ru}
                    </button>{' '}
                    <span className="font-mono text-xs text-muted-foreground">{f.code}</span>
                  </Td>
                  <Td>
                    <Badge variant="muted">{f.dataType}</Badge>
                    {f.enumSet ? (
                      <span className="ml-1 font-mono text-xs text-muted-foreground">
                        {f.enumSet}
                      </span>
                    ) : null}
                    {f.refKind ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        {t(`admin.refKinds.${f.refKind}` as 'admin.refKinds.issuers', {
                          defaultValue: f.refKind,
                        })}
                      </span>
                    ) : null}
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {f.levels.map((l) => t(`admin.levels.${l}`)).join(', ')}
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {t('adminMeta.fields.usage', {
                      types: f.usage.types,
                      records: f.usage.records,
                    })}
                  </Td>
                  <Td>
                    <div className="flex items-center justify-end gap-2">
                      {f.core ? <Badge variant="muted">{t('adminMeta.fields.core')}</Badge> : null}
                      <OriginBadge origin={f.origin} customized={f.customized} />
                      <ActiveToggle
                        label={f.name.ru}
                        active={f.active}
                        disabled={f.core}
                        onChange={(active) =>
                          action.run('fields', 'update', { code: f.code, active })
                        }
                      />
                      {f.core ? null : (
                        <RemoveButton
                          label={f.name.ru}
                          usage={f.usage.records}
                          onRemove={() => action.run('fields', 'remove', { code: f.code })}
                        />
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(',', '.')))

function FieldForm({
  data,
  field,
  onDone,
}: {
  data: AdminMeta
  field?: AdminField
  onDone: () => void
}) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const [code, setCode] = useState(field?.code ?? '')
  const [name, setName] = useState<LocalizedName>(field?.name ?? { ru: '' })
  const [dataType, setDataType] = useState<DataType>(field?.dataType ?? 'text')
  const [levels, setLevels] = useState<FieldLevel[]>(field?.levels ?? ['catalog'])
  const [enumSet, setEnumSet] = useState(field?.enumSet ?? '')
  const [refKind, setRefKind] = useState<string>(field?.refKind ?? 'issuers')
  const [listOf, setListOf] = useState<string>(field?.listOf ?? 'text')
  const [unit, setUnit] = useState(field?.unit ?? '')
  const [ui, setUi] = useState<Ui>(
    field
      ? {
          section: field.ui.section,
          showInTable: field.ui.showInTable,
          filterable: field.ui.filterable,
          sortable: field.ui.sortable,
        }
      : { section: 'main', showInTable: false, filterable: true, sortable: false },
  )
  const [min, setMin] = useState(field?.validation.min?.toString() ?? '')
  const [max, setMax] = useState(field?.validation.max?.toString() ?? '')
  const [maxLength, setMaxLength] = useState(field?.validation.maxLength?.toString() ?? '')
  const sets = [...new Set(data.enumOptions.map((o) => o.setCode))].sort()
  const numeric = dataType === 'integer' || dataType === 'decimal'
  const textual = dataType === 'text' || dataType === 'longtext'
  const validation = {
    ...(numeric && num(min) !== undefined ? { min: num(min) } : {}),
    ...(numeric && num(max) !== undefined ? { max: num(max) } : {}),
    ...(textual && num(maxLength) ? { maxLength: num(maxLength) } : {}),
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {field ? `${field.name.ru} (${field.code})` : t('adminMeta.fields.new')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (field)
              action.run(
                'fields',
                'update',
                { code: field.code, name, unit: unit || null, ui, validation },
                onDone,
              )
            else
              action.run(
                'fields',
                'create',
                {
                  code,
                  name,
                  dataType,
                  levels,
                  enumSet: enumSet || null,
                  refKind: dataType === 'ref' ? refKind : null,
                  listOf: dataType === 'list' ? listOf : null,
                  unit: unit || null,
                  validation,
                  ui,
                },
                onDone,
              )
          }}
        >
          {field ? null : (
            <>
              <Field label={t('adminMeta.code')} hint={t('adminMeta.codeHint')}>
                <Input
                  value={code}
                  required
                  pattern="[a-z][a-z0-9_]*"
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              <Field label={t('admin.dataType')}>
                <Select value={dataType} onChange={(e) => setDataType(e.target.value as DataType)}>
                  {DataType.options.map((d) => (
                    <option key={d} value={d}>
                      {t(`adminMeta.dataTypes.${d}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}
          <NameInputs value={name} onChange={setName} />
          {field ? null : (
            <fieldset className="grid gap-1.5 sm:col-span-2">
              <legend className="text-sm font-medium">{t('admin.level')}</legend>
              <div className="flex flex-wrap gap-4">
                {FieldLevel.options.map((l) => (
                  <label key={l} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={levels.includes(l)}
                      onChange={(e) =>
                        setLevels(e.target.checked ? [...levels, l] : levels.filter((x) => x !== l))
                      }
                    />
                    {t(`admin.levels.${l}`)}
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          {!field && (dataType === 'enum' || dataType === 'multi_enum') ? (
            <>
              <Field label={t('adminMeta.fields.enumSet')} hint={t('adminMeta.fields.enumSetHint')}>
                <Input
                  value={enumSet}
                  list="enum-sets"
                  required
                  pattern="[a-z][a-z0-9_]*"
                  onChange={(e) => setEnumSet(e.target.value)}
                />
              </Field>
              <datalist id="enum-sets">
                {sets.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </>
          ) : null}
          {!field && dataType === 'ref' ? (
            <Field label={t('adminMeta.fields.refKind')}>
              <Select value={refKind} onChange={(e) => setRefKind(e.target.value)}>
                {EditableRefKind.options.map((k) => (
                  <option key={k} value={k}>
                    {t(`admin.refKinds.${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {!field && dataType === 'list' ? (
            <Field label={t('adminMeta.fields.listOf')}>
              <Select value={listOf} onChange={(e) => setListOf(e.target.value)}>
                {ListItemType.options.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {numeric ? (
            <>
              <Field label={t('adminMeta.fields.unit')}>
                <Input value={unit} maxLength={16} onChange={(e) => setUnit(e.target.value)} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label={t('adminMeta.fields.min')}>
                  <Input inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} />
                </Field>
                <Field label={t('adminMeta.fields.max')}>
                  <Input inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} />
                </Field>
              </div>
            </>
          ) : null}
          {textual ? (
            <Field label={t('adminMeta.fields.maxLength')}>
              <Input
                inputMode="numeric"
                value={maxLength}
                onChange={(e) => setMaxLength(e.target.value)}
              />
            </Field>
          ) : null}
          <Field label={t('adminMeta.section')}>
            <Select
              value={ui.section}
              onChange={(e) => setUi({ ...ui, section: e.target.value as FieldSection })}
            >
              {FieldSection.options.map((s) => (
                <option key={s} value={s}>
                  {t(`sections.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
            {(['showInTable', 'filterable', 'sortable'] as const).map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={ui[k]}
                  onChange={(e) => setUi({ ...ui, [k]: e.target.checked })}
                />
                {t(`adminMeta.fields.${k}`)}
              </label>
            ))}
          </div>
          <div className="sm:col-span-2">
            <AdminError error={action.error} />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={action.pending}>
              {field ? t('common.save') : t('adminMeta.create')}
            </Button>
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
